import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { 
  Bomb, Plus, Trash2, Edit2, Save, DollarSign,
  Activity, Zap, Archive, Hash,
} from 'lucide-react';
import { toast } from 'sonner';
import { logErrorToast } from '@/lib/errorLogger';
import { DraftSabotageConfig, RARITY_COST_GUIDE } from '@/types/draftSabotageSettings';
import { SabotageRarity, RARITY_CONFIG, SABOTAGE_RARITIES } from '@/types/sabotageStats';
import { useDraftSabotage } from '@/contexts/DraftSabotageContext';
import { useLeague } from '@/contexts/LeagueContext';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

const rarityOptions: { value: SabotageRarity; label: string; cost: number }[] = 
  SABOTAGE_RARITIES.map(r => ({
    value: r,
    label: RARITY_CONFIG[r].label,
    cost: RARITY_COST_GUIDE[r],
  }));

interface DraftSabotageSettingsProps {
  readOnly?: boolean;
}

const DraftSabotageSettings = ({ readOnly = false }: DraftSabotageSettingsProps) => {
  const { 
    settings, activeSabotages, updateSabotage, 
    addSabotage: addSabotageToContext, 
    deleteSabotage: deleteSabotageFromContext, 
    toggleSabotageStatus, updateMaxPerDraft 
  } = useDraftSabotage();
  const { activeLeague, refreshLeague } = useLeague();
  
  const [editingSabotage, setEditingSabotage] = useState<DraftSabotageConfig | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newSabotage, setNewSabotage] = useState<Partial<DraftSabotageConfig>>({
    name: '', description: '', effect: '', cost: 5,
    rarity: 'common', icon: '⚡', color: 'warning', isActive: false,
  });

  const inactiveSabotages = settings.sabotages.filter(s => !s.isActive);
  const canAddActive = activeSabotages.length < settings.maxActive;
  const canAddInactive = inactiveSabotages.length < settings.maxInactive;

  const handleRarityChange = (rarity: SabotageRarity, target: 'new' | 'edit') => {
    const cost = RARITY_COST_GUIDE[rarity];
    const colorMap: Record<SabotageRarity, DraftSabotageConfig['color']> = {
      common: 'warning',
      rare: 'primary', 
      legendary: 'damage',
    };
    if (target === 'new') {
      setNewSabotage(prev => ({ ...prev, rarity, cost, color: colorMap[rarity] }));
    } else if (editingSabotage) {
      setEditingSabotage({ ...editingSabotage, rarity, cost, color: colorMap[rarity] });
    }
  };

  const handleToggleSabotageStatus = (id: string) => {
    const sabotage = settings.sabotages.find(s => s.id === id);
    if (!sabotage) return;
    const success = toggleSabotageStatus(id);
    if (success) {
      setHasChanges(true);
      toast[sabotage.isActive ? 'info' : 'success'](
        `${sabotage.name} ${sabotage.isActive ? 'moved to inactive' : 'is now active'}`
      );
    } else {
      logErrorToast(`Maximum ${settings.maxActive} active draft sabotages allowed`);
    }
  };

  const handleDeleteSabotage = (id: string) => {
    const sabotage = settings.sabotages.find(s => s.id === id);
    deleteSabotageFromContext(id);
    setHasChanges(true);
    toast.success(`${sabotage?.name} deleted`);
  };

  const handleUpdateSabotage = (updated: DraftSabotageConfig) => {
    updateSabotage(updated);
    setEditingSabotage(null);
    setHasChanges(true);
    toast.success(`${updated.name} updated`);
  };

  const handleAddSabotage = () => {
    if (!newSabotage.name || !newSabotage.description || !newSabotage.effect) {
      logErrorToast('Name, description, and effect are required');
      return;
    }
    const targetIsActive = newSabotage.isActive ?? false;
    if (targetIsActive && !canAddActive) {
      logErrorToast(`Maximum ${settings.maxActive} active draft sabotages allowed`);
      return;
    }
    if (!targetIsActive && !canAddInactive) {
      logErrorToast(`Maximum ${settings.maxInactive} inactive draft sabotages allowed`);
      return;
    }

    const sabotage: DraftSabotageConfig = {
      id: `ds${Date.now()}`,
      name: newSabotage.name,
      description: newSabotage.description,
      effect: newSabotage.effect,
      cost: newSabotage.cost || 5,
      rarity: newSabotage.rarity || 'common',
      icon: newSabotage.icon || '⚡',
      color: (newSabotage.color as DraftSabotageConfig['color']) || 'warning',
      isActive: targetIsActive,
      timesUsed: 0,
      totalCostCollected: 0,
      createdAt: new Date(),
    };

    addSabotageToContext(sabotage);
    setNewSabotage({
      name: '', description: '', effect: '', cost: 5,
      rarity: 'common', icon: '⚡', color: 'warning', isActive: false,
    });
    setIsAddDialogOpen(false);
    setHasChanges(true);
    toast.success(`${sabotage.name} created`);
  };

  const saveDraftSabotages = async () => {
    if (!activeLeague) {
      logErrorToast('No league selected');
      return;
    }
    setIsSaving(true);
    try {
      const existingConfig = (activeLeague.category_config as any) || {};
      const { error } = await supabase
        .from('leagues')
        .update({
          category_config: { ...existingConfig, draft_sabotages: settings },
        })
        .eq('id', activeLeague.id);
      if (error) throw error;
      setHasChanges(false);
      await refreshLeague();
      toast.success('Draft sabotage settings saved!');
    } catch {
      logErrorToast('Failed to save draft sabotage settings');
    } finally {
      setIsSaving(false);
    }
  };

  const stats = {
    totalUsed: settings.sabotages.reduce((sum, s) => sum + s.timesUsed, 0),
    totalCollected: settings.sabotages.reduce((sum, s) => sum + s.totalCostCollected, 0),
  };

  const getRarityBadge = (rarity: SabotageRarity) => {
    const config = RARITY_CONFIG[rarity];
    return (
      <Badge variant="outline" className={cn("text-xs", config.badgeClass)}>
        {config.label}
      </Badge>
    );
  };

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'damage': return 'border-damage/50 bg-damage/10';
      case 'warning': return 'border-warning/50 bg-warning/10';
      case 'success': return 'border-success/50 bg-success/10';
      default: return 'border-primary/50 bg-primary/10';
    }
  };

  const renderSabotageCard = (sabotage: DraftSabotageConfig) => {
    const rarityConfig = RARITY_CONFIG[sabotage.rarity];
    return (
      <Card 
        key={sabotage.id} 
        className={cn(
          "transition-all hover:shadow-md",
          sabotage.isActive 
            ? `${rarityConfig.borderClass} ${rarityConfig.glowClass} border bg-card` 
            : 'border-muted bg-muted/20 opacity-75'
        )}
      >
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-start gap-3">
            <div className={cn(
              "w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-xl sm:text-2xl shrink-0",
              sabotage.isActive ? getColorClasses(sabotage.color) : 'bg-muted'
            )}>
              {sabotage.icon}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                <h3 className="font-semibold text-sm sm:text-base truncate">{sabotage.name}</h3>
                {getRarityBadge(sabotage.rarity)}
                <Badge variant="outline" className="shrink-0 text-gold border-gold/50 text-[10px] sm:text-xs">
                  ${sabotage.cost}
                </Badge>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 mb-1.5">{sabotage.description}</p>
              <p className="text-xs text-primary font-medium mb-1.5">Effect: {sabotage.effect}</p>
              <div className="flex items-center gap-3 text-[10px] sm:text-xs text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1">
                  <Activity className="w-3 h-3" />
                  Used {sabotage.timesUsed}x
                </span>
                <span className="flex items-center gap-1 text-gold">
                  <DollarSign className="w-3 h-3" />
                  ${sabotage.totalCostCollected} collected
                </span>
                <span className="text-[10px] sm:text-xs">
                  Limit: {rarityConfig.limitPeriod === 'season' ? `${rarityConfig.weeklyLimit}/season` : rarityConfig.limitPeriod === 'draft' ? `${rarityConfig.weeklyLimit}/draft` : `${rarityConfig.weeklyLimit}/week`}
                  {rarityConfig.isGlobal && ' · 🌍 Global'}
                </span>
              </div>
            </div>

            {/* Actions — commish only */}
            {!readOnly && (
              <div className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-2 shrink-0 ml-1">
                <div className="flex items-center gap-1">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8"
                      onClick={() => setEditingSabotage(sabotage)}>
                      <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Edit Draft Sabotage</DialogTitle>
                      <DialogDescription>Modify the draft sabotage settings</DialogDescription>
                    </DialogHeader>
                    {editingSabotage && (
                      <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Icon (emoji)</Label>
                            <Input value={editingSabotage.icon}
                              onChange={(e) => setEditingSabotage({ ...editingSabotage, icon: e.target.value })}
                              className="w-20" />
                          </div>
                          <div className="space-y-2">
                            <Label>Rarity</Label>
                            <Select value={editingSabotage.rarity}
                              onValueChange={(v: SabotageRarity) => handleRarityChange(v, 'edit')}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {rarityOptions.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label} (~${opt.cost})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Name</Label>
                          <Input value={editingSabotage.name}
                            onChange={(e) => setEditingSabotage({ ...editingSabotage, name: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label>Description</Label>
                          <Textarea value={editingSabotage.description}
                            onChange={(e) => setEditingSabotage({ ...editingSabotage, description: e.target.value })}
                            rows={2} />
                        </div>
                        <div className="space-y-2">
                          <Label>Effect Summary</Label>
                          <Input value={editingSabotage.effect}
                            onChange={(e) => setEditingSabotage({ ...editingSabotage, effect: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label>Cost (from sabotage balance)</Label>
                          <Input type="number" min={1} value={editingSabotage.cost}
                            onChange={(e) => setEditingSabotage({ ...editingSabotage, cost: parseInt(e.target.value) || 1 })} />
                          <p className="text-xs text-muted-foreground">
                            Suggested: ${RARITY_COST_GUIDE[editingSabotage.rarity]} for {RARITY_CONFIG[editingSabotage.rarity].label}. Override if needed.
                          </p>
                        </div>
                      </div>
                    )}
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setEditingSabotage(null)}>Cancel</Button>
                      <Button onClick={() => editingSabotage && handleUpdateSabotage(editingSabotage)}>
                        <Save className="w-4 h-4 mr-2" />Save Changes
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 text-damage hover:text-damage">
                      <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Draft Sabotage?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete &quot;{sabotage.name}&quot; and all its stats.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDeleteSabotage(sabotage.id)}
                        className="bg-damage hover:bg-damage/90">
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                </div>

                <Switch checked={sabotage.isActive}
                  onCheckedChange={() => handleToggleSabotageStatus(sabotage.id)} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-display font-bold flex items-center gap-2">
            💣 Draft Sabotages
          </h2>
          <p className="text-sm text-muted-foreground">
            {readOnly 
              ? 'Sabotages available during the draft. Costs deducted from your sabotage balance'
              : 'Configure sabotages available during the draft'}
          </p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={saveDraftSabotages}
              disabled={!hasChanges || isSaving}
              className={hasChanges ? "border-primary text-primary" : ""}
            >
              <Save className="w-4 h-4 mr-1" />
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm"><Plus className="w-4 h-4 mr-2" />Add Draft Sabotage</Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Draft Sabotage</DialogTitle>
                <DialogDescription>Add a custom sabotage for use during the draft</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Icon (emoji)</Label>
                    <Input value={newSabotage.icon}
                      onChange={(e) => setNewSabotage({ ...newSabotage, icon: e.target.value })}
                      className="w-20" />
                  </div>
                  <div className="space-y-2">
                    <Label>Rarity</Label>
                    <Select value={newSabotage.rarity}
                      onValueChange={(v: SabotageRarity) => handleRarityChange(v, 'new')}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {rarityOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label} (~${opt.cost})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input value={newSabotage.name}
                    onChange={(e) => setNewSabotage({ ...newSabotage, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea value={newSabotage.description}
                    onChange={(e) => setNewSabotage({ ...newSabotage, description: e.target.value })}
                    rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Effect Summary *</Label>
                  <Input value={newSabotage.effect}
                    onChange={(e) => setNewSabotage({ ...newSabotage, effect: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Cost (from sabotage balance)</Label>
                  <Input type="number" min={1} value={newSabotage.cost}
                    onChange={(e) => setNewSabotage({ ...newSabotage, cost: parseInt(e.target.value) || 1 })} />
                  <p className="text-xs text-muted-foreground">Auto-suggested based on rarity. Override if needed.</p>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div>
                    <p className="font-medium">Start as Active</p>
                    <p className="text-xs text-muted-foreground">
                      {canAddActive ? `${activeSabotages.length}/${settings.maxActive} active slots used` : 'No active slots available'}
                    </p>
                  </div>
                  <Switch checked={newSabotage.isActive}
                    onCheckedChange={(checked) => setNewSabotage({ ...newSabotage, isActive: checked })}
                    disabled={!canAddActive && !newSabotage.isActive} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleAddSabotage}><Plus className="w-4 h-4 mr-2" />Create Sabotage</Button>
              </DialogFooter>
            </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Stats Overview */}
      <Card className="bg-gradient-to-r from-warning/10 via-primary/5 to-transparent border-warning/30">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-primary">{activeSabotages.length}/{settings.maxActive}</p>
              <p className="text-xs text-muted-foreground">Active Sabotages</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{settings.maxPerDraft}</p>
              <p className="text-xs text-muted-foreground">Max Per Draft</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-success">{stats.totalUsed}</p>
              <p className="text-xs text-muted-foreground">Total Times Used</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gold">${stats.totalCollected}</p>
              <p className="text-xs text-muted-foreground">Revenue Generated</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Max Per Draft — commish only */}
      {!readOnly && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/20">
                  <Hash className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <Label className="font-medium">Max Sabotages Per Draft</Label>
                  <p className="text-xs text-muted-foreground">Limit per team per draft session</p>
                </div>
              </div>
              <Input type="number" min={1} max={10} value={settings.maxPerDraft}
                onChange={(e) => updateMaxPerDraft(parseInt(e.target.value) || 1)}
                className="w-20 h-9 text-center" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rarity & Limits Guide */}
      <Card>
        <CardContent className="p-4">
          <p className="text-sm font-medium mb-3">Rarity Tiers & Limits</p>
          <div className="grid grid-cols-3 gap-3">
            {rarityOptions.map(opt => {
              const config = RARITY_CONFIG[opt.value];
              return (
                <div key={opt.value} className={cn(
                  "p-3 rounded-lg border text-center",
                  config.borderClass,
                  config.glowClass,
                )}>
                  <Badge variant="outline" className={cn("text-xs mb-2", config.badgeClass)}>
                    {config.label}
                  </Badge>
                  <p className="text-sm font-bold">${opt.cost}</p>
                  <p className="text-xs text-muted-foreground">
                    {config.limitPeriod === 'season' ? `${config.weeklyLimit}/season` : config.limitPeriod === 'draft' ? `${config.weeklyLimit}/draft` : `${config.weeklyLimit}/week`}
                    {config.isGlobal && <span className="block text-[10px] mt-0.5">🌍 Affects all teams</span>}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Active */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">Active Draft Sabotages</h3>
          <Badge variant="outline">{activeSabotages.length}/{settings.maxActive}</Badge>
        </div>
        {activeSabotages.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-8 text-center text-muted-foreground">
              <Bomb className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No active draft sabotages</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">{activeSabotages.map(renderSabotageCard)}</div>
        )}
      </div>

      <Separator />

      {/* Inactive */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Archive className="w-5 h-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-muted-foreground">Inactive Draft Sabotages</h3>
          <Badge variant="secondary">{inactiveSabotages.length}/{settings.maxInactive}</Badge>
        </div>
        {inactiveSabotages.length === 0 ? (
          <Card className="border-dashed border-muted">
            <CardContent className="p-6 text-center text-muted-foreground">
              <p className="text-sm">No inactive draft sabotages</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">{inactiveSabotages.map(renderSabotageCard)}</div>
        )}
      </div>

      {/* Info */}
      <Card className="border-muted">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <Bomb className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">How Draft Sabotages Work</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Costs deducted from your <strong>sabotage balance</strong></li>
                <li>Common ($5) & Rare ($15): <strong>1/week</strong> · Legendary ($35): <strong>1/draft, affects ALL teams 🌍</strong></li>
                <li>Each team can use up to <strong>{settings.maxPerDraft}</strong> total sabotages per draft</li>
                <li>Revenue from purchases goes to the prize pool</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DraftSabotageSettings;
